import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { Download, ArrowLeft, Phone, Loader2, Smartphone, Gift, PhoneIncoming } from 'lucide-react';
import toast from 'react-hot-toast';
import { useVenue } from '@core/contexts/VenueContext';
import { useProfile } from '@core/contexts/ProfileContext';
import { coreRuntime } from '@core/runtime';
import { useIdentity } from '@core/contexts/IdentityContext';
import { saveUserPhone, type PhoneProof, type SavePhoneResult } from '@core/api/users';
import { grantConsent } from '@core/api/consent';
import { getLoyaltySettings } from '@core/api/loyaltySettings';
import type { TierProgressResponse, VenueLoyaltySettingsPublic } from '@lunit/shared-types';
import { Card } from '@core/components/Card';
import { Skeleton } from '@core/components/Skeleton';
import { TierProgress } from '@core/components/TierProgress';
import { Checkbox } from '@core/components/Checkbox';
import { PhoneCodeForm } from '@core/components/PhoneCodeForm';
import { formatPhone, phoneErrorMessage } from '@core/utils/phone';

// «Моя карта» — QR для кассы (barcode = телефон без кода страны, совпадает с yTimes).
// Ветка «привяжите телефон» доступна только там, где среда умеет requestContact
// (mini-app); в PWA клиент вошёл по телефону — телефон есть всегда.
// Привязка: сначала подписанный контакт Telegram, запасной путь — номер и код из звонка.
// Если номер был у карточки PWA этого заведения, сервер переносит её историю сюда (merged).
export function QRCodePage() {
  const navigate = useNavigate();
  const { venue } = useVenue();
  const { user, loading: profileLoading, refresh } = useProfile();
  const identity = useIdentity();
  const capabilities = coreRuntime().capabilities;

  const [tierProgress, setTierProgress] = useState<TierProgressResponse | null>(null);
  const [loyaltySettings, setLoyaltySettings] = useState<VenueLoyaltySettingsPublic | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [savingPhone, setSavingPhone] = useState(false);
  const [manualPhoneOpen, setManualPhoneOpen] = useState(false);
  const [subscribingMarketing, setSubscribingMarketing] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true); // Default checked for better conversion

  useEffect(() => {
    if (!user) return;
    void coreRuntime().fetchTierProgress(identity).then(setTierProgress).catch(() => undefined);
    void getLoyaltySettings().then(setLoyaltySettings).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /**
   * Телефон → barcode для QR (10 цифр без кода страны):
   * +79689943259 → «9689943259». Совпадает с barcode в yTimes на кассе.
   */
  const getPhoneBarcode = (phone: string | undefined | null): string | null => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && (digits[0] === '7' || digits[0] === '8')) {
      return digits.slice(1);
    }
    if (digits.length === 10) {
      return digits;
    }
    return null;
  };

  useEffect(() => {
    if (profileLoading) return;
    const barcode = getPhoneBarcode(user?.phone);
    if (!barcode) {
      setQrDataUrl('');
      setLoading(false);
      return;
    }
    setLoading(true);
    QRCode.toDataURL(barcode, {
      width: 250,
      margin: 4, // Larger margin for better contrast and scanning
      color: { dark: '#000000', light: '#FFFFFF' },
      errorCorrectionLevel: 'L', // Low error correction = larger blocks, easier to scan
    })
      .then((dataUrl: string) => {
        setQrDataUrl(dataUrl);
        setError(null);
      })
      .catch((err: unknown) => {
        console.error('Failed to generate QR code:', err);
        setError('Не удалось сгенерировать QR код. Обновите страницу.');
      })
      .finally(() => setLoading(false));
  }, [user?.phone, profileLoading]);

  const handleDownload = useCallback(() => {
    if (!qrDataUrl || !user) {
      return;
    }
    const link = document.createElement('a');
    link.download = `lunit-loyalty-${user.id}.png`;
    link.href = qrDataUrl;
    link.click();
  }, [qrDataUrl, user]);

  // Привязка телефона (только в среде с requestContact — mini-app): общий хвост для контакта и кода.
  const savePhone = useCallback(
    async (proof: PhoneProof): Promise<SavePhoneResult | null> => {
      if (!user) return null;
      // 1. Сохранить номер (сервер верит только доказательству: подписанный контакт или код звонка)
      const result = await saveUserPhone(user.telegramId, proof);

      // 2. Номер был у карточки из приложения (PWA) — её баланс и история теперь здесь
      if (result.merged) {
        toast.success(`Мы объединили вашу карточку из приложения: баланс ${result.balance ?? 0}`, { duration: 6000 });
      }

      // 3. Согласие на рассылку, если галочка стоит
      const linkedText = 'Телефон привязан! Называйте его на кассе для получения бонусов';
      if (marketingConsent) {
        try {
          const { bonusAwarded } = await grantConsent(user.id, 'MARKETING', '1.0');
          if (bonusAwarded > 0) {
            toast.success(`Телефон привязан! +${bonusAwarded} бонусов за подписку 🎁`);
          } else if (!result.merged) {
            toast.success(linkedText);
          }
        } catch (consentErr) {
          console.error('Failed to grant marketing consent:', consentErr);
          if (!result.merged) toast.success(linkedText);
        }
      } else if (!result.merged) {
        toast.success(linkedText);
      }

      void refresh();
      return result;
    },
    [user, marketingConsent, refresh]
  );

  const handleRequestPhone = useCallback(() => {
    const rt = coreRuntime();
    if (!rt.requestPhoneContact || !user) {
      toast.error('Привязка телефона недоступна');
      return;
    }

    rt.requestPhoneContact(async (phoneNumber: string, signedContact?: string) => {
      setSavingPhone(true);
      try {
        await savePhone({ phone: phoneNumber, contact: signedContact });
      } catch (err) {
        console.error('Failed to save phone:', err);
        toast.error(phoneErrorMessage(err), { duration: 6000 });
      } finally {
        setSavingPhone(false);
      }
    });
  }, [user, savePhone]);

  // Запасной путь: номер и код из звонка (ошибку показывает сама форма).
  const handleConfirmCode = useCallback(
    async (phone: string, code: string) => {
      await savePhone({ phone, code });
      setManualPhoneOpen(false);
    },
    [savePhone]
  );

  // Подписка на маркетинг, когда телефон уже привязан
  const handleSubscribeMarketing = useCallback(async () => {
    if (!user) return;

    setSubscribingMarketing(true);
    try {
      const { bonusAwarded } = await grantConsent(user.id, 'MARKETING', '1.0');
      if (bonusAwarded > 0) {
        toast.success(`Вы подписались! +${bonusAwarded} бонусов 🎁`);
      } else {
        toast.success('Вы подписались на акции и скидки!');
      }
      void refresh();
    } catch (err) {
      console.error('Failed to subscribe to marketing:', err);
      toast.error('Не удалось подписаться. Попробуйте позже.');
    } finally {
      setSubscribingMarketing(false);
    }
  }, [user, refresh]);

  if (!profileLoading && !user) {
    return (
      <div className="min-h-screen bg-page p-4">
        <Card>
          <p className="text-center text-text-muted">Авторизуйтесь в приложении, чтобы увидеть ваш QR код.</p>
        </Card>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen bg-page p-4">
        <Card>
          <p className="text-center text-text-muted">Заведение не найдено. Откройте приложение по ссылке заведения.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page">
      <div className="container mx-auto px-4 py-8 max-w-md">
        <Card className="text-center space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => navigate('/')}
              className="p-2 -ml-2 text-text-muted active:scale-95 transition-transform rounded-full hover:bg-secondary"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold text-text-main">Ваша карта лояльности</h1>
            <div className="w-10" /> {/* Spacer to center title */}
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-error bg-error/10 border border-error/20 rounded-brand px-3 py-2">{error}</p>
          )}

          {/* QR Code or Phone Required Placeholder */}
          {loading || profileLoading ? (
            <Skeleton className="w-[250px] h-[250px] mx-auto rounded-lg" />
          ) : qrDataUrl ? (
            // Phone linked - show QR code
            <div className="relative inline-block">
              <div className="bg-card p-2 rounded-lg shadow-lg inline-block">
                <img src={qrDataUrl} alt="Ваш QR код" className="rounded" />
              </div>
              <div className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full font-medium">
                Активен
              </div>
            </div>
          ) : capabilities.requestContact ? (
            // No phone - show placeholder with CTA (только в среде Telegram)
            <div className="space-y-4">
              <div className="w-[250px] h-[250px] mx-auto bg-secondary/50 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-text-muted/30">
                <Smartphone size={64} className="text-text-muted/50 mb-4" />
                <p className="text-text-muted text-center px-6 text-sm">
                  Привяжите телефон
                </p>
              </div>

              <p className="text-text-muted/70 text-sm text-center px-4">
                Чтобы мы узнавали вас на кассе 💛
              </p>

              {/* Marketing consent checkbox */}
              {(loyaltySettings?.marketingConsentBonus ?? 50) > 0 && (
                <label className="flex items-start gap-3 px-4 py-3 bg-secondary/50 rounded-brand cursor-pointer active:bg-secondary/70 transition-colors">
                  <Checkbox
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-text-main">
                    Получать акции и скидки{' '}
                    <span className="inline-flex items-center gap-1 text-primary font-medium">
                      (+{loyaltySettings?.marketingConsentBonus ?? 50} <Gift size={14} className="inline" />)
                    </span>
                  </span>
                </label>
              )}

              <button
                onClick={handleRequestPhone}
                disabled={savingPhone}
                className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-primary text-primary-foreground rounded-brand font-medium transition-colors hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
              >
                {savingPhone ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Phone size={20} />
                )}
                Поделиться контактом
              </button>

              {/* Запасной путь, если Telegram не отдаёт контакт: номер и код из звонка */}
              {manualPhoneOpen ? (
                <div className="space-y-3 text-left border-t border-border pt-4">
                  <PhoneCodeForm onConfirm={handleConfirmCode} confirmLabel="Привязать номер" />
                  <button
                    type="button"
                    onClick={() => setManualPhoneOpen(false)}
                    className="w-full text-sm text-text-muted underline"
                  >
                    Отмена
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setManualPhoneOpen(true)}
                  disabled={savingPhone}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-secondary text-text-on-secondary rounded-brand font-medium transition-colors hover:bg-secondary/80 active:scale-[0.98] disabled:opacity-50"
                >
                  <PhoneIncoming size={18} />
                  Ввести номер — звонок с кодом
                </button>
              )}
            </div>
          ) : (
            <p className="text-sm text-text-muted px-4">
              Телефон не указан — обратитесь на кассе, чтобы привязать номер к карте.
            </p>
          )}

          {/* Marketing consent CTA - show when phone is bound but no marketing consent */}
          {qrDataUrl && user && !user.marketingConsentGranted && (loyaltySettings?.marketingConsentBonus ?? 50) > 0 && (
            <div className="bg-primary/10 border border-primary/20 rounded-brand p-4 text-center">
              <p className="text-lg mb-2">☕ Будем на связи?</p>
              <p className="text-sm text-text-muted mb-2">
                Скидки, акции и подарки —<br />
                отправляем только подписчикам
              </p>
              <p className="text-sm font-semibold text-primary mb-3">
                +{loyaltySettings?.marketingConsentBonus ?? 50} бонусов сейчас
              </p>
              <button
                onClick={handleSubscribeMarketing}
                disabled={subscribingMarketing}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-brand font-medium transition-colors hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
              >
                {subscribingMarketing ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : null}
                Да, подписаться 💛
              </button>
            </div>
          )}

          {/* User Data - uses text-on-secondary for proper contrast on light bg */}
          <div className="bg-secondary rounded-brand px-4 py-3 text-left">
            {/* Barcode / Card Number - show if phone exists (matches yTimes barcode) */}
            {user?.phone && (
              <div className="flex justify-between text-sm mb-2">
                <span className="text-text-on-secondary-muted">Номер карты:</span>
                <span className="font-mono font-semibold text-text-on-secondary">{getPhoneBarcode(user.phone)}</span>
              </div>
            )}

            {/* Phone Row - show formatted phone */}
            {user?.phone && (
              <div className="flex justify-between text-sm mb-2">
                <span className="text-text-on-secondary-muted">Телефон:</span>
                <span className="font-semibold text-text-on-secondary">{formatPhone(user.phone)}</span>
              </div>
            )}

            <div className="flex justify-between text-sm mb-2">
              <span className="text-text-on-secondary-muted">Баланс:</span>
              <span className="font-semibold text-text-on-secondary">{user?.balance ?? 0} pts</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-text-on-secondary-muted">Статус:</span>
              <span className="font-semibold uppercase text-primary">{tierProgress?.currentTier?.name || user?.tier || 'Bronze'}</span>
            </div>
          </div>

          {/* Save Button - only when phone is linked and QR exists */}
          {qrDataUrl && (
            <button
              onClick={handleDownload}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-secondary text-text-on-secondary rounded-brand font-medium transition-colors hover:bg-secondary/80 active:scale-[0.98]"
            >
              <Download size={20} />
              Сохранить QR-код
            </button>
          )}

          {/* Tier Progress */}
          {tierProgress && (
            <div className="mt-4">
              <TierProgress tierProgress={tierProgress} />
            </div>
          )}

          {/* Footer hint */}
          <p className="text-sm text-text-muted">
            {user?.phone
              ? 'Показывайте QR-код или называйте номер телефона на кассе для начисления бонусов.'
              : 'Привяжите телефон, чтобы начать копить и тратить бонусы.'}
          </p>
        </Card>
      </div>
    </div>
  );
}
